/**
 * Cookie-consent state management (Task #202 — GDPR banner).
 *
 * Source of truth for the visitor's per-category cookie choices. The
 * choice is persisted in a first-party cookie (`aisl_consent_v1`) so
 * it survives reloads without a server round-trip; each choice is
 * also POSTed to /api/v1/cookie-consents for the server-side audit
 * log.
 *
 * Bumping `CONSENT_VERSION` invalidates every existing cookie and
 * re-prompts every visitor automatically.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { loadPlausible, unloadPlausible } from "./analytics";

export const CONSENT_VERSION = 1;
const CONSENT_COOKIE = "aisl_consent_v1";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year

export interface ConsentCategories {
  analytics: boolean;
  marketing: boolean;
}

export interface ConsentState {
  version: number;
  categories: ConsentCategories;
  /** ISO-8601 string. */
  timestamp: string;
}

interface CookieConsentContextValue {
  /** `null` means the visitor has not made a choice yet (or it's a stale version). */
  state: ConsentState | null;
  /** True when the banner should be visible. */
  needsChoice: boolean;
  /** True when the preferences modal is open. */
  preferencesOpen: boolean;
  setPreferencesOpen: (open: boolean) => void;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  savePreferences: (categories: ConsentCategories) => void;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

// ─────────── cookie helpers ───────────

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf("=");
    if (eq < 0) continue;
    if (trimmed.slice(0, eq) === name) {
      try {
        return decodeURIComponent(trimmed.slice(eq + 1));
      } catch {
        return trimmed.slice(eq + 1);
      }
    }
  }
  return null;
}

function writeCookie(name: string, value: string): void {
  if (typeof document === "undefined") return;
  const isHttps = window.location.protocol === "https:";
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    "path=/",
    `max-age=${COOKIE_MAX_AGE_SECONDS}`,
    "SameSite=Lax",
  ];
  if (isHttps) parts.push("Secure");
  document.cookie = parts.join("; ");
}

function readStoredState(): ConsentState | null {
  const raw = readCookie(CONSENT_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (
      typeof parsed.version === "number" &&
      parsed.version === CONSENT_VERSION &&
      parsed.categories &&
      typeof parsed.categories.analytics === "boolean" &&
      typeof parsed.categories.marketing === "boolean" &&
      typeof parsed.timestamp === "string"
    ) {
      return {
        version: parsed.version,
        categories: {
          analytics: parsed.categories.analytics,
          marketing: parsed.categories.marketing,
        },
        timestamp: parsed.timestamp,
      };
    }
  } catch {
    // fall through
  }
  return null;
}

// ─────────── audit log POST ───────────

async function postAuditRecord(state: ConsentState): Promise<void> {
  try {
    await fetch(`${import.meta.env.BASE_URL}api/v1/cookie-consents`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        bannerVersion: state.version,
        choice: state.categories,
        pageUrl: typeof window !== "undefined" ? window.location.href : undefined,
      }),
    });
  } catch {
    // Audit-log failure is non-fatal — the visitor's local cookie
    // already records their choice. Swallow so a network blip never
    // surfaces as a UI error.
  }
}

// ─────────── provider ───────────

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConsentState | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // Hydrate from cookie on mount (client-only).
  useEffect(() => {
    const stored = readStoredState();
    setState(stored);
    setHydrated(true);
    if (stored?.categories.analytics) {
      loadPlausible();
    }
  }, []);

  // Whenever consent flips, mount or unmount Plausible.
  useEffect(() => {
    if (!hydrated || !state) return;
    if (state.categories.analytics) {
      loadPlausible();
    } else {
      unloadPlausible();
    }
  }, [hydrated, state]);

  const persist = useCallback((categories: ConsentCategories) => {
    const next: ConsentState = {
      version: CONSENT_VERSION,
      categories,
      timestamp: new Date().toISOString(),
    };
    writeCookie(CONSENT_COOKIE, JSON.stringify(next));
    setState(next);
    setPreferencesOpen(false);
    void postAuditRecord(next);
  }, []);

  const acceptAll = useCallback(() => {
    persist({ analytics: true, marketing: true });
  }, [persist]);

  const rejectNonEssential = useCallback(() => {
    persist({ analytics: false, marketing: false });
  }, [persist]);

  const savePreferences = useCallback(
    (categories: ConsentCategories) => persist(categories),
    [persist],
  );

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      state,
      // Don't show the banner until we've checked the cookie (avoids a
      // flash on every reload for visitors who already consented).
      needsChoice: hydrated && state === null,
      preferencesOpen,
      setPreferencesOpen,
      acceptAll,
      rejectNonEssential,
      savePreferences,
    }),
    [state, hydrated, preferencesOpen, acceptAll, rejectNonEssential, savePreferences],
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error("useCookieConsent must be used within <CookieConsentProvider>");
  }
  return ctx;
}
